import type { CommentMentionInboxItem } from '@seen/core/models/inbox'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { cn } from '@seen/ui'
import type { JSX, ReactNode } from 'react'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'
import { useCommentParentResource } from '#src/hooks/inbox/useCommentParentResource.ts'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useTimelineItemUrl } from '#src/hooks/useTimelineItemUrl.ts'
import { InboxRow } from '#src/pages/inbox/row/InboxRow.tsx'
import { InboxRowAvatar } from '#src/pages/inbox/row/InboxRowAvatar.tsx'
import { InboxRowContent } from '#src/pages/inbox/row/InboxRowContent.tsx'
import { InboxRowIcon } from '#src/pages/inbox/row/InboxRowIcon.tsx'
import { InboxRowSubtext } from '#src/pages/inbox/row/InboxRowSubtext.tsx'
import { InboxRowText } from '#src/pages/inbox/row/InboxRowText.tsx'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

export interface CommentMentionInboxItemRowProps {
  item: CommentMentionInboxItem
  notificationItem: NotificationItem
  onSelect?: () => void
  isSelected?: boolean
  sidebarOpen?: boolean
}

export function CommentMentionInboxItemRow({
  item,
  notificationItem,
  onSelect,
  isSelected,
  sidebarOpen,
}: CommentMentionInboxItemRowProps): ReactNode {
  const {
    comment,
    parentResourceType,
    relatedTask,
    relatedOrder,
    patient,
    isLoading,
  } = useCommentParentResource(item.comment)

  const hasRelatedTask = parentResourceType === 'Task' || !!relatedTask
  const hasRelatedOrder =
    parentResourceType === 'ServiceRequest' || !!relatedOrder
  const { data: practitioner } = useRead(
    ExtendedPractitioner,
    comment?.practitionerSender
  )

  const commentUrl = useTimelineItemUrl(comment)

  const orderTitle = getOrderTitle(relatedOrder)

  return (
    <InboxRow
      to={commentUrl}
      notificationItem={notificationItem}
      onSelect={onSelect}
      isSelected={isSelected}
      sidebarOpen={sidebarOpen}
    >
      <InboxRowAvatar
        profile={comment?.practitionerSender}
        icon={
          <InboxRowIcon className="-right-0.75 -bottom-0.75 border-none bg-white group-hover:bg-accent group-focus:bg-accent">
            <IconMention />
          </InboxRowIcon>
        }
      />
      <InboxRowContent>
        <InboxRowText
          notificationItem={notificationItem}
          isLoading={isLoading}
          badges={
            <>
              {patient && (
                <ParticipantTaskRowBadge
                  className="[&>button]:h-[1.55rem] [&>button]:py-0"
                  participant={patient.reference}
                />
              )}
              {relatedOrder && (
                <RowBadge
                  icon={
                    <IconOrderStatus
                      status={relatedOrder?.status}
                      isSubmitting={relatedOrder?.isSubmitting}
                      className="size-3.5"
                    />
                  }
                >
                  {relatedOrder?.seenIdentifier} {!!orderTitle && orderTitle}
                </RowBadge>
              )}
            </>
          }
        >
          {hasRelatedTask && (
            <>
              {relatedTask?.priority && (
                <TaskPriorityIcon
                  priority={relatedTask.priority}
                  isTaskCompletedOrCancelled={
                    relatedTask.isCompletedOrCancelled
                  }
                  className={cn('-mt-0.75 mr-0.75 inline')}
                />
              )}
              {relatedTask?.state && (
                <IconTaskState
                  state={relatedTask.state}
                  className="-mt-0.75 mr-0.75 inline"
                />
              )}
              <span>{relatedTask?.title ?? '[deleted]'}</span>
            </>
          )}
          {!hasRelatedTask &&
            extractTextFromHTML(comment?.message ?? '[deleted comment]')}
        </InboxRowText>
        <InboxRowSubtext>
          {!!practitioner && (
            <>
              {practitioner?.name}
              {!!practitioner?.roles?.length && (
                <> {practitioner?.roles?.join(', ')}</>
              )}{' '}
              {!hasRelatedTask && 'mentioned you'}
              {hasRelatedTask && (
                <>
                  mentioned you:{' '}
                  {extractTextFromHTML(comment?.message ?? '[deleted comment]')}
                </>
              )}
            </>
          )}
          {!practitioner && (
            <>
              {!hasRelatedTask && 'You were mentioned'}
              {hasRelatedTask && (
                <>
                  You were mentioned:{' '}
                  {extractTextFromHTML(comment?.message ?? '[deleted comment]')}
                </>
              )}
            </>
          )}
          {!hasRelatedTask &&
            !hasRelatedOrder &&
            patient?.name &&
            ` \u00b7 ${patient.name}`}
        </InboxRowSubtext>
      </InboxRowContent>
    </InboxRow>
  )
}

function IconMention({ className }: { className?: string }): JSX.Element {
  // Icon from linear.app
  return (
    <svg
      className={className}
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="lch(39.48% 1 282.863 / 1)"
      role="img"
      focusable="false"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M8.35729 15C9.39338 15 10.5562 14.7872 11.3514 14.5417C11.6762 14.4415 11.8313 14.0905 11.7239 13.7679C11.6077 13.4188 11.2294 13.2321 10.8745 13.3296C10.241 13.5036 9.32655 13.6993 8.4316 13.6993C4.58432 13.6993 2.52062 11.6633 2.52062 8.01921C2.52062 4.52881 4.56145 2.27323 8.22009 2.27323C11.5643 2.27323 13.5594 4.33673 13.5537 7.12466C13.5594 9.50647 13.2393 10.5766 12.1989 10.5766C11.61 10.5766 11.2842 10.1156 11.2842 9.5394V5.14963C11.2842 4.7613 10.9694 4.44649 10.5811 4.44649H10.4926C10.1531 4.44649 9.87791 4.72168 9.87791 5.06115H9.80359C9.6321 4.5782 8.6374 4.14465 7.44263 4.28185C5.79053 4.46844 4.37281 5.78009 4.37281 7.94238C4.37281 10.198 5.5733 11.7072 7.4998 11.7785C8.80318 11.8334 9.64925 11.2517 9.91793 10.6041H9.98653C10.1237 11.4877 11.1184 11.8773 12.096 11.8444C14.537 11.8005 15 9.49549 15 7.17954C15 3.87573 12.6448 1 8.27726 1C3.69824 1 1.00572 3.68365 1 8.00823C1.00572 12.459 3.62964 15 8.35729 15ZM7.83136 10.4339C6.33932 10.4339 5.82483 9.20463 5.81911 7.87652C5.82483 6.61427 6.56227 5.60996 7.85423 5.60996C9.2548 5.60996 9.88363 6.20267 9.88934 7.87103C9.89506 9.5833 9.31768 10.4339 7.83136 10.4339Z"></path>
    </svg>
  )
}
